import { queryKeys } from '@/constant/common'
import type { QueryClient } from '@tanstack/react-query'

/**
 * SCRUM-1032 QC round 7, item 11 — the wait after an MK2 import, made visible.
 *
 * The import reports success well before its decoders can be read: 420
 * addresses took about 23 s to appear on D2. The wait ran inside the import
 * modal, which closes 1.5 s after success, so nothing on screen knew it was
 * happening and the list said "0 decoders found" for the whole of it.
 *
 * The wait now publishes a flag in the query cache, keyed by interface, which
 * outlives the modal. The decoder list reads it (`useMk2ImportSettling`).
 */

export const mk2SettlingKey = (interfaceId: number) => [
  queryKeys.mk2ImportSettling,
  Number(interfaceId),
]

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * - `changed`: the count moved off a known pre-import baseline — the
 *   controller has answered for the import.
 * - `stable`: there was no baseline (the modal re-attached to a running import,
 *   or the read before it failed), and two reads in a row agreed. That says the
 *   list has stopped moving, not that the import landed.
 * - `timeout`: nothing conclusive before the ceiling.
 */
export type SettleOutcome = 'changed' | 'stable' | 'timeout'

/**
 * Polls the decoder count until it moves off `startedWith` or the time runs
 * out. A read that fails answers null ("no news"). Each read is raced against
 * the time left, so a request that never answers cannot hold the wait past
 * its ceiling.
 *
 * SCRUM-1032 round 7 review: with no baseline, any first successful read used
 * to count as "changed" — three seconds in, before the decoders arrived, which
 * set off the automatic resync early. Without a baseline the wait now runs
 * until two reads agree instead.
 */
export const waitForImportedDecoders = async ({
  readCount,
  startedWith,
  pollMs,
  timeoutMs,
}: {
  readCount: () => Promise<number | null>
  startedWith: number | null
  pollMs: number
  timeoutMs: number
}): Promise<SettleOutcome> => {
  const deadline = Date.now() + timeoutMs
  let previous: number | null = null
  while (Date.now() < deadline) {
    await sleep(pollMs)
    const remaining = deadline - Date.now()
    if (remaining <= 0) break
    const count = await Promise.race([
      readCount().catch(() => null),
      sleep(remaining).then(() => null),
    ])
    if (count === null) continue
    if (startedWith !== null) {
      if (count !== startedWith) return 'changed'
      continue
    }
    if (previous !== null && count === previous) return 'stable'
    previous = count
  }
  return 'timeout'
}

/**
 * Whether the automatic resync may follow the wait.
 *
 * With a pre-import baseline it follows every outcome, as decided on 7 Oct
 * ("after the settle poll": ordering, not a gate). Without one the wait could
 * not tell an arrived import from a list that simply was not moving, so the
 * resync — which deletes every decoder the controller does not report back —
 * is skipped; the manual Resync is still there.
 */
export const shouldAutoResync = (
  outcome: SettleOutcome,
  startedWith: number | null,
): boolean => startedWith !== null && outcome !== 'stable'

/**
 * Settles `work` within `ms`, whichever comes first. Used to bound a request
 * that has no timeout of its own, so a flag held around it always clears.
 */
export const withinTime = async (
  work: Promise<unknown>,
  ms: number,
): Promise<void> => {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    await Promise.race([
      work.catch(() => undefined),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, ms)
      }),
    ])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/**
 * How many runs hold each interface's flag, per query client. Two runs can
 * overlap (two imports back to back, or one import reported done twice), and
 * the first to finish must not lower the flag while the other still works.
 */
const holders = new WeakMap<QueryClient, Map<number, number>>()

const holdersOf = (queryClient: QueryClient) => {
  let counts = holders.get(queryClient)
  if (!counts) {
    counts = new Map()
    holders.set(queryClient, counts)
  }
  return counts
}

/**
 * Raises the settling flag for each interface, runs `work`, and lowers the
 * flag again — on success, on timeout and on an error alike — once no other
 * run still holds it.
 */
export const runWhileSettling = async (
  queryClient: QueryClient,
  interfaceIds: number[],
  work: () => Promise<unknown>,
): Promise<void> => {
  const ids = [...new Set(interfaceIds.filter((id) => Number.isFinite(id)))]
  const counts = holdersOf(queryClient)
  ids.forEach((id) => {
    counts.set(id, (counts.get(id) ?? 0) + 1)
    queryClient.setQueryData(mk2SettlingKey(id), true)
  })
  try {
    await work()
  } finally {
    ids.forEach((id) => {
      const left = (counts.get(id) ?? 1) - 1
      if (left > 0) {
        counts.set(id, left)
        return
      }
      counts.delete(id)
      queryClient.setQueryData(mk2SettlingKey(id), false)
    })
  }
}
